import { useCallback } from 'react';

import { toast, ToasterProps } from 'sonner';

import { CloseIcon } from 'components/icons/v2';

interface UseToastReturnValue {
  handleToast: (title: string, type: 'success' | 'error', description?: string, icon?: JSX.Element,) => void;
}

interface ToastElProps {
  title: string;
  description?: string;
  icon?: JSX.Element;
  id: string | number;
  type: 'success' | 'error';
}

const toastOptions: ToasterProps['toastOptions'] = {
  duration: 5000,
  className: 'w-full',
  classNames: {
    toast: 'p-0 bg-transparent border-0 rounded-radius-2xl'
  }
};

const toastEl = (props: ToastElProps): JSX.Element => {
  const {
    description,
    icon,
    id,
    title,
    type
  } = props;

  const toastStyles = (toastType: ToastElProps['type']) => {
    switch (toastType) {
      case 'success':
        return {
          containerClassnames: 'bg-success-50 border-success-50',
          progressContainerClassnames: 'bg-success-100',
          progressClassnames: 'bg-success-500',
          titleClassnames: 'text-success-950',
          descriptionClassnames: 'text-success-900',
          iconClassnames: 'text-success-600',
          closeIconClassnames: 'text-success-500'
        };
      case 'error':
        return {
          containerClassnames: 'bg-error-50 border-error-50',
          progressContainerClassnames: 'bg-error-100',
          progressClassnames: 'bg-error-500',
          titleClassnames: 'text-error-950',
          descriptionClassnames: 'text-error-900',
          iconClassnames: 'text-error-600',
          closeIconClassnames: 'text-error-500'
        };
    }
  };

  return (
    <div
      className={`flex flex-col w-full gap-4 shadow-lg border rounded-radius-2xl max-w-[400px] overflow-hidden ${toastStyles(type).containerClassnames}`}>
      <div className={`w-full h-2 ${toastStyles(type).progressContainerClassnames}`}>
        <div className={`animate-[width_5s] h-full ${toastStyles(type).progressClassnames}`}></div>
      </div>
      <div className="w-full flex gap-4 items-start px-4 pb-4">
        {!!icon && (
          <div className={toastStyles(type).iconClassnames}>
            {icon}
          </div>
        )}
        <div className="flex flex-col flex-1 gap-1">
          <p className={`text-small font-medium ${toastStyles(type).titleClassnames}`}>{title}</p>
          {!!description && (
            <p className={`text-small ${toastStyles(type).descriptionClassnames}`}>{description}</p>
          )}
        </div>
        <div className="cursor-pointer" onClick={() => toast.dismiss(id)}>
          <CloseIcon height={20} width={20} />
        </div>
      </div>
    </div>
  );
};

export const useToast = (): UseToastReturnValue => {

  const handleToast: UseToastReturnValue['handleToast'] = useCallback((
    title,
    type,
    description?,
    icon?,
  ) => {
    toast.custom((id) => toastEl({
      type,
      description,
      title,
      icon,
      id
    }), toastOptions);
  }, []);

  return {
    handleToast,
  };
};
